import { useRollbar } from '@rollbar/react';
import dynamic from 'next/dynamic';
import { useEffect, useState } from 'react';
import EcommOptionsList from '@/components/EcommOptionsList/EcommOptionsList';
import FooterComponent from '@/components/Layout/Footer/Footer';
import HeaderComponent from '@/components/Layout/Header/Header';
import { Carousel } from '@/components/ProductPageBase/Carousel/Carousel';
import { getDescriptionPlacement } from '@/components/ProductPageBase/descriptionPlacement';
import ProductPageBase from '@/components/ProductPageBase/ProductPageBase';
import { Section } from '@/components/Section/Section';
import type { EnahancedProductPageContent } from '@/contentful/api/enhancePages/productPage.types';
import type {
  GlobalFooterContent,
  GlobalNavContent,
} from '@/contentful/contentful-exports';
import { trackEvents } from '@/utils/analytics';
import type { SHOPIFY_ENV } from '../../../clients/storefront/storefront.types';
import useStyles from './SunrunShopProductPage.styles';
import type { SunrunShopProductPageCheckoutButtonsProps } from './SunrunShopProductPageCheckoutButtons';
import {
  formatDisplayPrice,
  formatSubscriptionLabel,
  getFirstAvailableVariantId,
  getFirstSellingPlanId,
  getSelectedPurchaseData,
  getSellingPlanAllocations,
  isMultiSellingPlan,
  moveElementToFront,
} from './sunrunShopProductPage.utils';

const DEFAULT_VARIANT_OPTIONS_HEADING = 'Options';

const CheckoutButtons = dynamic<SunrunShopProductPageCheckoutButtonsProps>(
  () => import('./SunrunShopProductPageCheckoutButtons'),
  { ssr: false }
);

interface Props {
  content: EnahancedProductPageContent;
}

export default function SunrunShopProductPage({ content }: Props) {
  const { classes } = useStyles();
  const rollbar = useRollbar();
  const { fields } = content;
  const { topSections, product, bottomSections, header, footer, shopifyEnv } =
    fields;
  const variants = product?.variants?.nodes;
  const initialVariantId = getFirstAvailableVariantId(variants);

  const [selectedVariantId, setSelectedVariantId] = useState<
    string | undefined
  >(initialVariantId);
  const [selectedSellingPlanId, setSelectedSellingPlanId] = useState<
    string | undefined
  >(() =>
    getFirstSellingPlanId(variants?.find((v) => v.id === initialVariantId))
  );

  useEffect(() => {
    const ref = new URLSearchParams(window.location.search).get('ref');

    trackEvents({
      event: 'product_page_viewed',
      ref,
      product_name: product?.title,
      // this is automatically tracked by gtm but they want it in utc format
      time_triggered: new Date().toUTCString(),
    });

    if (product?.requiresSellingPlan && ref) {
      rollbar.info('Subscription product upsell page view', {
        ref,
        productTitle: product?.title,
      });
    }

    return () => {
      trackEvents({
        event: 'product_page_exited',
        ref,
        product_name: product?.title,
        time_triggered: new Date().toUTCString(),
      });
    };
  }, [product?.title, product?.requiresSellingPlan, rollbar]);

  const buyNowButtonLabel = fields?.buyNowButtonLabel;
  const addToCartButtonLabel = fields?.addToCartButtonLabel;
  const {
    variantOptionsListHeading = DEFAULT_VARIANT_OPTIONS_HEADING,
    sellingPlanOptionsListHeading = DEFAULT_VARIANT_OPTIONS_HEADING,
  } = fields;
  const isOnDark = fields.style === 'onDark';

  const [isDisclaimerAcknowledged, setIsDisclaimerAcknowledged] =
    useState(false);
  const requireDisclaimerCheckbox = Boolean(fields.requireDisclaimerCheckbox);
  const [images, setImages] = useState<string[]>(
    product?.images?.nodes?.map((node) => node.url as string) || []
  );
  const [hoveredImage, setHoveredImage] = useState<string>();

  const { selectedVariant, totalPriceAmount } = getSelectedPurchaseData({
    variants,
    selectedVariantId,
    selectedSellingPlanId,
    requiresSellingPlan: product?.requiresSellingPlan,
  });
  const isMultiVariant = variants && variants.length > 1;
  const shouldShowSellingPlanPicker = isMultiSellingPlan(
    product?.requiresSellingPlan,
    selectedVariant
  );
  const totalPrice = formatDisplayPrice({
    totalPriceAmount,
    selectedVariant,
    isSubscriptionProduct: Boolean(product?.requiresSellingPlan),
  });

  const onSelect = (variantId: string) => {
    setSelectedVariantId(variantId);

    const newVariant = variants?.find((v) => v.id === variantId);
    setSelectedSellingPlanId(getFirstSellingPlanId(newVariant));

    const urlToMove = newVariant?.image?.url as string;

    if (urlToMove) {
      setImages((currentImages) =>
        moveElementToFront(currentImages, urlToMove)
      );
    }
  };

  const onHover = (hoveredId: string, isEntering: boolean) => {
    if (isEntering) {
      const hoveredVariant = variants?.find((v) => v.id === hoveredId);
      if (hoveredVariant?.quantityAvailable && hoveredVariant?.image?.url) {
        setHoveredImage(hoveredVariant?.image.url as string);
      }
    } else {
      setHoveredImage(undefined);
    }
  };

  return (
    <>
      <HeaderComponent header={header as unknown as GlobalNavContent} />
      {topSections?.map((topSection, index) => (
        <Section
          key={topSection.sys.id}
          section={topSection}
          sectionPosition={index}
        />
      ))}
      <ProductPageBase
        heading={product?.title || ''}
        isOnDark={isOnDark}
        subheading={fields.subheading || ''}
        imageContent={
          <Carousel
            selectedImageOverride={hoveredImage}
            images={images}
            isOnDark={isOnDark}
          />
        }
        descriptionHtml={(product?.descriptionHtml as string) || ''}
        descriptionPlacement={getDescriptionPlacement(
          fields.productDescriptionPlacement
        )}
        optionCardLists={[
          ...(isMultiVariant
            ? [
                <EcommOptionsList
                  key='variant-options-list'
                  heading={variantOptionsListHeading}
                  label={variantOptionsListHeading}
                  name='options'
                  isOnDark={isOnDark}
                  items={variants?.map(({ id, title, quantityAvailable }) => ({
                    id,
                    label: title,
                    disabled: !quantityAvailable,
                  }))}
                  selectedId={selectedVariantId}
                  onSelect={onSelect}
                  onHover={onHover}
                  optionsContainerClassName={classes.optionsContainer}
                  cardContentClassName={classes.cardContent}
                />,
              ]
            : []),
          ...(shouldShowSellingPlanPicker && selectedVariant
            ? [
                <EcommOptionsList
                  key='subscription-options-list'
                  heading={sellingPlanOptionsListHeading}
                  label={sellingPlanOptionsListHeading}
                  name='selling-plan-options'
                  isOnDark={isOnDark}
                  items={getSellingPlanAllocations(selectedVariant).map(
                    (allocation) => ({
                      id: allocation.sellingPlan.id,
                      label: formatSubscriptionLabel(allocation),
                    })
                  )}
                  selectedId={selectedSellingPlanId}
                  onSelect={setSelectedSellingPlanId}
                  optionsContainerClassName={classes.optionsContainer}
                  cardContentClassName={classes.cardContent}
                />,
              ]
            : []),
        ]}
        totalPrice={totalPrice}
        checkoutButtons={[
          <CheckoutButtons
            key='checkout-buttons'
            variants={variants}
            selectedVariantId={selectedVariantId}
            selectedSellingPlanId={selectedSellingPlanId}
            requiresSellingPlan={product?.requiresSellingPlan}
            buyNowButtonLabel={buyNowButtonLabel}
            addToCartButtonLabel={addToCartButtonLabel}
            storefrontEnv={shopifyEnv as SHOPIFY_ENV}
            isOnDark={isOnDark}
            productTitle={product?.title}
            subscriptionProductType={fields.subscriptionProductType}
            requireDisclaimerCheckbox={requireDisclaimerCheckbox}
            isDisclaimerAcknowledged={isDisclaimerAcknowledged}
          />,
        ]}
        disclaimer={fields.disclaimer || ''}
        requireDisclaimerCheckbox={requireDisclaimerCheckbox}
        disclaimerCheckboxTerms={fields.disclaimerCheckboxTerms}
        isDisclaimerAcknowledged={isDisclaimerAcknowledged}
        onDisclaimerAcknowledgedChange={setIsDisclaimerAcknowledged}
      />
      {bottomSections?.map((bottomSection, index) => (
        <Section
          key={bottomSection.sys.id}
          section={bottomSection}
          sectionPosition={index}
        />
      ))}
      <FooterComponent footer={footer as unknown as GlobalFooterContent} />
    </>
  );
}
